import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { http, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  Loader2,
  Plus,
  Copy,
  Power,
  PowerOff,
  UserPlus,
  Users,
  MousePointerClick,
  DollarSign,
} from "lucide-react";

function StatPill({ children }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-sm bg-ink-50 border border-ink-100 px-2 py-0.5 text-[11px] text-ink-700 font-mono">
      {children}
    </span>
  );
}

function policyLabel(p) {
  const mode = p?.discount_mode || "lifetime";
  if (mode === "lifetime") return "Lifetime";
  if (mode === "first_purchase") return "First purchase";
  if (mode === "subscription_months")
    return `Sub · first ${p.discount_duration_months || 3} mo`;
  if (mode === "days_after_signup")
    return `${p.discount_duration_days || 90} days`;
  return mode;
}

export default function AdminAffiliates() {
  const [loading, setLoading] = useState(true);
  const [partners, setPartners] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    code: "",
    discount_cap_pct: 20,
    initial_discount_pct: "",
    notes: "",
    discount_mode: "lifetime",
    discount_duration_months: 3,
    discount_duration_days: 90,
  });
  const [freshCreds, setFreshCreds] = useState(null);
  const [editing, setEditing] = useState(null); // partner obj being edited
  const [editForm, setEditForm] = useState({
    discount_cap_pct: 0,
    active: true,
    discount_mode: "lifetime",
    discount_duration_months: 3,
    discount_duration_days: 90,
  });
  const [signupsFor, setSignupsFor] = useState(null); // partner obj whose signups to view
  const [signupsLoading, setSignupsLoading] = useState(false);
  const [signupsData, setSignupsData] = useState(null); // { partner, referrals }

  const openSignups = async (p) => {
    setSignupsFor(p);
    setSignupsLoading(true);
    setSignupsData(null);
    try {
      const { data } = await http.get(`/admin/affiliates/${p.id}/referrals`);
      setSignupsData(data);
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load signups"
      );
      setSignupsFor(null);
    } finally {
      setSignupsLoading(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/admin/affiliates");
      setPartners(data.partners || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load partners");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        discount_cap_pct: Number(form.discount_cap_pct),
        notes: form.notes.trim() || null,
        discount_mode: form.discount_mode,
        discount_duration_months: Number(form.discount_duration_months) || 3,
        discount_duration_days: Number(form.discount_duration_days) || 90,
      };
      if (form.code.trim()) payload.code = form.code.trim();
      if (form.initial_discount_pct !== "" && !Number.isNaN(Number(form.initial_discount_pct))) {
        payload.initial_discount_pct = Number(form.initial_discount_pct);
      }
      const { data } = await http.post("/admin/affiliates", payload);
      toast.success(`Partner ${data.partner.code} created.`);
      setFreshCreds({
        email: data.partner.email,
        password: data.initial_password,
        user_existed: !!data.user_existed,
        login_url: data.login_url,
        share_url: data.share_url,
      });
      setForm({
        name: "",
        email: "",
        code: "",
        discount_cap_pct: 20,
        initial_discount_pct: "",
        notes: "",
        discount_mode: "lifetime",
        discount_duration_months: 3,
        discount_duration_days: 90,
      });
      setOpenCreate(false);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to create partner");
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (p) => {
    try {
      await http.patch(`/admin/affiliates/${p.id}`, { active: !p.active });
      toast.success(p.active ? "Partner paused." : "Partner reactivated.");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to update partner");
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    try {
      await http.patch(`/admin/affiliates/${editing.id}`, {
        discount_cap_pct: Number(editForm.discount_cap_pct),
        discount_mode: editForm.discount_mode,
        discount_duration_months: Number(editForm.discount_duration_months) || 3,
        discount_duration_days: Number(editForm.discount_duration_days) || 90,
      });
      toast.success("Partner updated.");
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to update partner");
    }
  };

  const copy = (val, msg = "Copied.") => {
    navigator.clipboard.writeText(val).then(() => toast.success(msg));
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="admin-affiliates-page">
      <div className="mb-6 flex items-center gap-3">
        <Link to="/admin" className="text-ink-500 hover:text-ink-900 text-xs uppercase tracking-widestx font-mono flex items-center gap-1">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to admin
        </Link>
      </div>

      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400">Admin</div>
          <h1 className="font-display text-3xl font-extrabold text-ink-900 mt-1">
            Affiliate partners
          </h1>
          <p className="text-sm text-ink-500 mt-1">
            Manually provision partners. They get a login, a unique share code, and see live stats in their dashboard.
          </p>
        </div>
        <Button
          onClick={() => setOpenCreate(true)}
          className="gap-2"
          data-testid="admin-add-partner-btn"
        >
          <Plus className="w-4 h-4" /> New partner
        </Button>
      </div>

      {loading ? (
        <div className="text-ink-400 text-sm flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading partners…
        </div>
      ) : partners.length === 0 ? (
        <div className="rounded-sm border border-dashed border-ink-100 bg-white px-6 py-16 text-center">
          <p className="text-ink-500 text-sm">No affiliate partners yet.</p>
          <Button onClick={() => setOpenCreate(true)} className="mt-4 gap-2">
            <Plus className="w-4 h-4" /> Create your first partner
          </Button>
        </div>
      ) : (
        <div className="rounded-sm border border-ink-100 bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widestx font-mono text-ink-400 border-b border-ink-100 bg-ink-50/50">
                  <th className="py-3 px-4">Partner</th>
                  <th className="py-3 px-4">Code</th>
                  <th className="py-3 px-4">Discount</th>
                  <th className="py-3 px-4">Cap</th>
                  <th className="py-3 px-4">Policy</th>
                  <th className="py-3 px-4">Clicks</th>
                  <th className="py-3 px-4">Signups</th>
                  <th className="py-3 px-4">Paid</th>
                  <th className="py-3 px-4">Revenue</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {partners.map((p) => (
                  <tr key={p.id} className="border-b border-ink-100/60 hover:bg-ink-50/40">
                    <td className="py-3 px-4">
                      <div className="font-medium text-ink-900">{p.name}</div>
                      <div className="text-xs text-ink-500">{p.email}</div>
                    </td>
                    <td className="py-3 px-4">
                      <button
                        onClick={() => copy(p.code, "Code copied.")}
                        className="inline-flex items-center gap-1 font-mono text-ink-900 hover:text-brand-700"
                        title="Copy code"
                      >
                        {p.code}
                        <Copy className="w-3 h-3 opacity-60" />
                      </button>
                    </td>
                    <td className="py-3 px-4 text-ink-900">{p.discount_pct}%</td>
                    <td className="py-3 px-4 text-ink-500">{p.discount_cap_pct}%</td>
                    <td className="py-3 px-4 text-ink-700 text-xs">{policyLabel(p)}</td>
                    <td className="py-3 px-4 text-ink-700">{p.stats.clicks}</td>
                    <td className="py-3 px-4 text-ink-700">
                      {p.stats.signups > 0 ? (
                        <button
                          onClick={() => openSignups(p)}
                          className="text-brand-700 hover:text-brand-900 hover:underline font-medium"
                          title="View who signed up"
                          data-testid={`partner-signups-count-${p.code}`}
                        >
                          {p.stats.signups}
                        </button>
                      ) : (
                        p.stats.signups
                      )}
                    </td>
                    <td className="py-3 px-4 text-ink-700">{p.stats.paid_conversions}</td>
                    <td className="py-3 px-4 text-ink-900 font-semibold">
                      ${p.stats.revenue_usd.toFixed(2)}
                    </td>
                    <td className="py-3 px-4">
                      {p.active ? (
                        <StatPill>
                          <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full" /> Active
                        </StatPill>
                      ) : (
                        <StatPill>
                          <span className="w-1.5 h-1.5 bg-rose-500 rounded-full" /> Paused
                        </StatPill>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => openSignups(p)}
                          className="p-1.5 text-ink-500 hover:text-ink-900 rounded-sm hover:bg-ink-50"
                          title="View signups"
                          data-testid={`partner-view-signups-${p.code}`}
                        >
                          <Users className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => copy(`${window.location.origin}/?ref=${p.code}`, "Share link copied.")}
                          className="p-1.5 text-ink-500 hover:text-ink-900 rounded-sm hover:bg-ink-50"
                          title="Copy share link"
                          data-testid={`partner-copy-share-${p.code}`}
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => {
                            setEditing(p);
                            setEditForm({
                              discount_cap_pct: p.discount_cap_pct,
                              active: p.active,
                              discount_mode: p.discount_mode || "lifetime",
                              discount_duration_months: p.discount_duration_months || 3,
                              discount_duration_days: p.discount_duration_days || 90,
                            });
                          }}
                          className="p-1.5 text-ink-500 hover:text-ink-900 rounded-sm hover:bg-ink-50"
                          title="Edit cap"
                        >
                          <UserPlus className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => toggleActive(p)}
                          className="p-1.5 text-ink-500 hover:text-ink-900 rounded-sm hover:bg-ink-50"
                          title={p.active ? "Pause" : "Reactivate"}
                          data-testid={`partner-toggle-${p.code}`}
                        >
                          {p.active ? <PowerOff className="w-3.5 h-3.5" /> : <Power className="w-3.5 h-3.5" />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create partner dialog */}
      <Dialog open={openCreate} onOpenChange={setOpenCreate}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Provision a new partner</DialogTitle>
            <DialogDescription>
              Creates a login account (role: partner) and a share code. The initial password is shown once — copy it and hand it to the partner.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={create} className="space-y-4">
            <div>
              <Label className="text-xs font-mono uppercase tracking-widestx text-ink-500">Name</Label>
              <Input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Acme Media"
                className="mt-1"
                data-testid="new-partner-name"
              />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-widestx text-ink-500">Login email</Label>
              <Input
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="partner@acme.com"
                className="mt-1"
                data-testid="new-partner-email"
              />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-widestx text-ink-500">
                Code (optional)
              </Label>
              <Input
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                placeholder="ACME10  (leave blank to auto-generate)"
                className="mt-1 font-mono"
                data-testid="new-partner-code"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-mono uppercase tracking-widestx text-ink-500">Cap %</Label>
                <Input
                  required
                  type="number"
                  min={0}
                  max={50}
                  step="0.5"
                  value={form.discount_cap_pct}
                  onChange={(e) => setForm({ ...form, discount_cap_pct: e.target.value })}
                  className="mt-1"
                  data-testid="new-partner-cap"
                />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase tracking-widestx text-ink-500">
                  Initial %
                </Label>
                <Input
                  type="number"
                  min={0}
                  max={50}
                  step="0.5"
                  value={form.initial_discount_pct}
                  onChange={(e) => setForm({ ...form, initial_discount_pct: e.target.value })}
                  placeholder={String(form.discount_cap_pct || 0)}
                  className="mt-1"
                />
              </div>
            </div>
            <div className="rounded-sm border border-ink-100 bg-ink-50/50 p-3 space-y-3">
              <div>
                <Label className="text-xs font-mono uppercase tracking-widestx text-ink-500">
                  Discount duration
                </Label>
                <select
                  value={form.discount_mode}
                  onChange={(e) => setForm({ ...form, discount_mode: e.target.value })}
                  className="mt-1 w-full rounded-sm border border-ink-100 bg-white px-3 py-2 text-sm text-ink-900 focus:outline-none focus:ring-2 focus:ring-brand"
                  data-testid="new-partner-discount-mode"
                >
                  <option value="lifetime">Lifetime — every purchase forever</option>
                  <option value="first_purchase">First purchase only</option>
                  <option value="subscription_months">
                    Subscription — first N months (packs excluded)
                  </option>
                  <option value="days_after_signup">
                    First N days after signup (all products)
                  </option>
                </select>
              </div>
              {form.discount_mode === "subscription_months" && (
                <div>
                  <Label className="text-xs font-mono uppercase tracking-widestx text-ink-500">
                    Months
                  </Label>
                  <select
                    value={form.discount_duration_months}
                    onChange={(e) => setForm({ ...form, discount_duration_months: e.target.value })}
                    className="mt-1 w-full rounded-sm border border-ink-100 bg-white px-3 py-2 text-sm text-ink-900"
                    data-testid="new-partner-duration-months"
                  >
                    <option value={1}>1 month</option>
                    <option value={3}>3 months</option>
                    <option value={6}>6 months</option>
                    <option value={12}>12 months</option>
                  </select>
                  <p className="mt-1 text-xs text-ink-500">
                    Only tier subscription renewals get the discount for this window. Credit / email / mobile packs pay full price.
                  </p>
                </div>
              )}
              {form.discount_mode === "days_after_signup" && (
                <div>
                  <Label className="text-xs font-mono uppercase tracking-widestx text-ink-500">
                    Days after signup
                  </Label>
                  <Input
                    type="number"
                    min={1}
                    max={730}
                    value={form.discount_duration_days}
                    onChange={(e) => setForm({ ...form, discount_duration_days: e.target.value })}
                    className="mt-1"
                    data-testid="new-partner-duration-days"
                  />
                  <p className="mt-1 text-xs text-ink-500">
                    Discount applies to every checkout within this window from the user's signup.
                  </p>
                </div>
              )}
              {form.discount_mode === "first_purchase" && (
                <p className="text-xs text-ink-500">
                  Discount applies to the referred user's first paid checkout only.
                </p>
              )}
              {form.discount_mode === "lifetime" && (
                <p className="text-xs text-ink-500">
                  Discount stays valid on every purchase this user ever makes — as long as this partner is active.
                </p>
              )}
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-widestx text-ink-500">Notes (internal)</Label>
              <Input
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="e.g. Podcast host — Q2 launch"
                className="mt-1"
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpenCreate(false)}
                disabled={creating}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={creating} className="gap-2" data-testid="new-partner-submit">
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Create partner
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Fresh-credentials modal — shown once after create */}
      <Dialog open={!!freshCreds} onOpenChange={(v) => !v && setFreshCreds(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {freshCreds?.user_existed
                ? "Partner linked to existing account"
                : "Partner provisioned — send them these"}
            </DialogTitle>
            <DialogDescription>
              {freshCreds?.user_existed ? (
                <>
                  This email already had a LeadIQ Pro account, so we promoted it to <b>partner</b> role.
                  The user keeps their existing password — share the login URL and their share link below.
                </>
              ) : (
                <>
                  This password is displayed <b>once</b>. Copy it now — you cannot see it again.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          {freshCreds && (
            <div className="space-y-3 text-sm">
              <CredRow label="Email" value={freshCreds.email} copyMsg="Email copied." />
              {freshCreds.password ? (
                <CredRow label="Password" value={freshCreds.password} copyMsg="Password copied." mono />
              ) : (
                <div className="rounded-sm border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                  No new password generated — this user already had an account. If they forgot their password,
                  ask them to use the <b>Forgot password</b> flow on the login page.
                </div>
              )}
              <CredRow label="Login URL" value={freshCreds.login_url} copyMsg="Login URL copied." />
              <CredRow label="Share link" value={freshCreds.share_url} copyMsg="Share link copied." mono />
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setFreshCreds(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit cap dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit {editing?.name}</DialogTitle>
            <DialogDescription>
              Adjust the max cap and duration policy. If a partner's current discount exceeds the new cap, we squeeze it back down automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-mono uppercase tracking-widestx text-ink-500">Cap %</Label>
              <Input
                type="number"
                min={0}
                max={50}
                step="0.5"
                value={editForm.discount_cap_pct}
                onChange={(e) => setEditForm({ ...editForm, discount_cap_pct: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-widestx text-ink-500">
                Discount duration
              </Label>
              <select
                value={editForm.discount_mode}
                onChange={(e) => setEditForm({ ...editForm, discount_mode: e.target.value })}
                className="mt-1 w-full rounded-sm border border-ink-100 bg-white px-3 py-2 text-sm text-ink-900"
                data-testid="edit-partner-discount-mode"
              >
                <option value="lifetime">Lifetime — every purchase forever</option>
                <option value="first_purchase">First purchase only</option>
                <option value="subscription_months">
                  Subscription — first N months (packs excluded)
                </option>
                <option value="days_after_signup">
                  First N days after signup (all products)
                </option>
              </select>
            </div>
            {editForm.discount_mode === "subscription_months" && (
              <div>
                <Label className="text-xs font-mono uppercase tracking-widestx text-ink-500">Months</Label>
                <select
                  value={editForm.discount_duration_months}
                  onChange={(e) => setEditForm({ ...editForm, discount_duration_months: e.target.value })}
                  className="mt-1 w-full rounded-sm border border-ink-100 bg-white px-3 py-2 text-sm text-ink-900"
                >
                  <option value={1}>1 month</option>
                  <option value={3}>3 months</option>
                  <option value={6}>6 months</option>
                  <option value={12}>12 months</option>
                </select>
              </div>
            )}
            {editForm.discount_mode === "days_after_signup" && (
              <div>
                <Label className="text-xs font-mono uppercase tracking-widestx text-ink-500">Days after signup</Label>
                <Input
                  type="number"
                  min={1}
                  max={730}
                  value={editForm.discount_duration_days}
                  onChange={(e) => setEditForm({ ...editForm, discount_duration_days: e.target.value })}
                  className="mt-1"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={saveEdit}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Signups drawer — who signed up under a given partner */}
      <Dialog
        open={!!signupsFor}
        onOpenChange={(v) => {
          if (!v) {
            setSignupsFor(null);
            setSignupsData(null);
          }
        }}
      >
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              Signups under{" "}
              <span className="font-mono text-brand-700">{signupsFor?.code}</span>
            </DialogTitle>
            <DialogDescription>
              Every user who signed up through{" "}
              {signupsFor?.name ? <b>{signupsFor.name}</b> : "this partner"}'s share
              link. Paid column shows whether they've converted on any purchase.
            </DialogDescription>
          </DialogHeader>
          {signupsLoading ? (
            <div className="flex items-center gap-2 text-ink-500 text-sm py-8 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading signups…
            </div>
          ) : signupsData?.referrals?.length ? (
            <div
              className="rounded-sm border border-ink-100 bg-white overflow-hidden"
              data-testid="signups-table"
            >
              <div className="max-h-[55vh] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-ink-50/90 backdrop-blur">
                    <tr className="text-left text-[10px] uppercase tracking-widestx font-mono text-ink-400 border-b border-ink-100">
                      <th className="py-2.5 px-3">User</th>
                      <th className="py-2.5 px-3">Signed up</th>
                      <th className="py-2.5 px-3">Tier</th>
                      <th className="py-2.5 px-3">Paid</th>
                      <th className="py-2.5 px-3 text-right">Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {signupsData.referrals.map((r) => (
                      <tr
                        key={r.user_id}
                        className="border-b border-ink-100/60 hover:bg-ink-50/40"
                      >
                        <td className="py-2.5 px-3">
                          <div className="font-medium text-ink-900">
                            {r.name || <span className="text-ink-400">—</span>}
                          </div>
                          <div className="text-xs text-ink-500 truncate max-w-[260px]">
                            {r.email || (
                              <span className="text-rose-500">(user deleted)</span>
                            )}
                          </div>
                        </td>
                        <td className="py-2.5 px-3 text-ink-700 text-xs">
                          {formatTs(r.attached_at || r.created_at)}
                        </td>
                        <td className="py-2.5 px-3 text-ink-700 text-xs">
                          {r.tier || <span className="text-ink-400">free</span>}
                        </td>
                        <td className="py-2.5 px-3">
                          {r.paid_conversions > 0 ? (
                            <StatPill>
                              <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
                              {r.paid_conversions}× paid
                            </StatPill>
                          ) : (
                            <span className="text-ink-400 text-xs">—</span>
                          )}
                        </td>
                        <td className="py-2.5 px-3 text-ink-900 font-semibold text-right">
                          {r.revenue_usd > 0
                            ? `$${Number(r.revenue_usd).toFixed(2)}`
                            : <span className="text-ink-300 font-normal">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="rounded-sm border border-dashed border-ink-100 bg-white px-6 py-12 text-center text-sm text-ink-500">
              No signups yet.
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setSignupsFor(null);
                setSignupsData(null);
              }}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function formatTs(ts) {
  if (!ts) return "—";
  try {
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return ts;
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return ts;
  }
}

function CredRow({ label, value, copyMsg, mono }) {
  return (
    <div className="rounded-sm border border-ink-100 bg-ink-50 p-3 flex items-center gap-3">
      <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-500 w-20 shrink-0">{label}</div>
      <div className={`flex-1 text-ink-900 truncate ${mono ? "font-mono text-sm" : ""}`}>{value}</div>
      <button
        onClick={() => navigator.clipboard.writeText(value).then(() => toast.success(copyMsg))}
        className="p-1.5 text-ink-500 hover:text-ink-900 rounded-sm hover:bg-white"
        title="Copy"
      >
        <Copy className="w-4 h-4" />
      </button>
    </div>
  );
}
